import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, Lock, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import BitAvatar from "@/components/bit/BitAvatar";
import Logo from "@/components/Logo";
import { formatApiError } from "@/lib/api";
import { getAdsConfig } from "@/components/AdBanner";

const POPUNDER_KEY = "devpath_popunder_shown";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Popunder deaktiviert, um störende Werbung beim Anklicken zu verhindern
    return;
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Willkommen zurück! 👋");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <Logo />
            <span className="font-heading font-bold text-xl text-foreground">DevPath</span>
          </Link>
          <h1 className="font-heading font-black text-3xl sm:text-4xl mb-2">Willkommen zurück</h1>
          <p className="text-muted-foreground font-medium mb-8">Melde dich an und lerne weiter.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="font-bold text-sm">E-Mail</label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={2.5} />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  className="w-full pl-10 pr-4 py-3 border-2 border-black rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="du@beispiel.de"
                />
              </div>
            </div>
            <div>
              <label className="font-bold text-sm">Passwort</label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={2.5} />
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="w-full pl-10 pr-12 py-3 border-2 border-black rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" strokeWidth={2.5} />
                  ) : (
                    <Eye className="h-4 w-4" strokeWidth={2.5} />
                  )}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full flex items-center justify-center gap-2 bg-primary text-white font-bold py-3 rounded-lg border-2 border-black nb-shadow nb-press disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} />}
              Anmelden
            </button>
          </form>

          <p className="text-center mt-3 font-medium text-sm">
            <Link to="/passwort-vergessen" className="text-muted-foreground hover:text-secondary underline" data-testid="goto-forgot">
              Passwort vergessen?
            </Link>
          </p>

          <p className="text-center mt-6 font-medium text-sm">
            Noch kein Konto?{" "}
            <Link to="/register" className="font-bold text-secondary underline" data-testid="goto-register">
              Jetzt registrieren
            </Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:flex relative items-center justify-center border-l border-line bg-card/60 overflow-hidden">
        <div className="absolute inset-x-12 top-1/2 h-0.5 bg-line" aria-hidden="true" />
        <div className="absolute inset-x-12 top-1/2 h-0.5 trace-flow" aria-hidden="true" />
        <div className="absolute inset-y-12 left-1/2 w-0.5 bg-line" aria-hidden="true" />
        <div className="relative flex flex-col items-center gap-6 rounded-[28px] border border-line bg-background/80 px-14 py-12">
          <div className="bit-bob">
            <BitAvatar size={200} mood="happy" />
          </div>
          <p className="max-w-xs text-center text-muted-foreground">
            Schön, dass du wieder da bist. <span className="text-foreground">Bit</span> hat deine Leiterbahnen schon vorgewärmt.
          </p>
        </div>
      </div>
    </div>
  );
}
